import type { Project } from "../../features/leaderboard/types/index.ts";
import type { AuditEvidence } from "../../features/project-detail/types/index.ts";
import { CATEGORY_WEIGHTS } from "../scoring/contract.ts";

export { CATEGORY_TOOLTIPS } from "../scoring/contract.ts";

export const PROVIDER_NAMES: Record<string, string> = {
  twitterscore: "Twitter Score",
  sorsa: "Sorsa (ex TweetScout)",
  moni: "Moni",
  momentum: "Momentum",
  raises: "Raises/Backers",
  defillama: "DefiLlama",
  github: "GitHub",
  pedigree: "Pedigree (AI)",
};

export const PROVIDER_DESCRIPTIONS: Record<string, string> = {
  twitterscore: "Twitter engagement & follower quality score",
  sorsa: "Sorsa social influence analysis (formerly TweetScout)",
  moni: "Moni on-chain social graph & wallet analysis",
  momentum: "Recent growth velocity across social & on-chain metrics",
  raises: "Fundraising rounds, investors, and total capital raised",
  defillama: "DefiLlama protocol revenue & TVL data",
  github: "GitHub repository activity, commits, and contributors",
  pedigree: "AI-powered founder background & track record analysis",
};

export function getConfidenceLevel(confidence: number): {
  className: string;
  label: string;
} {
  const pct = confidence * 100;
  if (pct >= 80) return { className: "confidence-high", label: "High" };
  if (pct >= 60) return { className: "confidence-medium", label: "Medium" };
  return { className: "confidence-low", label: "Low" };
}

export function getScoreClass(score: number | string | null): string {
  if (score === null) return "";
  const num = Number(score);
  if (num >= 70) return "high";
  if (num >= 40) return "medium";
  return "low";
}

export function getRankClass(index: number): string {
  if (index === 0) return "top-3 rank-1";
  if (index < 3) return "top-3";
  return "";
}

export function getScoreBreakdown(source: Project | AuditEvidence[]) {
  const clamp = (v: number) => Math.max(0, Math.min(1, v));
  const toNum = (v: number | string | null) => (v != null ? Number(v) : 0);

  if (!Array.isArray(source)) {
    return {
      social: toNum(source.social_points).toFixed(1),
      backers: toNum(source.backers_points).toFixed(1),
      pedigree: toNum(source.pedigree_points).toFixed(1),
      momentum: toNum(source.momentum_points).toFixed(1),
      dev: toNum(source.dev_points).toFixed(1),
      revenue: toNum(source.revenue_points).toFixed(1),
    };
  }

  const byProvider: Record<string, number> = {};
  for (const item of source) {
    if (item.sub_score !== null && byProvider[item.provider_slug] === undefined) {
      byProvider[item.provider_slug] = Number(item.sub_score);
    }
  }

  let socialSum = 0;
  let socialCount = 0;

  const sorsaScore = byProvider.sorsa ?? byProvider.tweetscout;
  if (sorsaScore != null) {
    socialSum += sorsaScore;
    socialCount++;
  }
  if (byProvider.twitterscore != null) {
    socialSum += byProvider.twitterscore;
    socialCount++;
  }
  if (byProvider.moni != null) {
    socialSum += byProvider.moni;
    socialCount++;
  }

  const baseSocial = socialCount > 0 ? socialSum / socialCount : 0;
  const communityQuality = byProvider.community != null ? byProvider.community : 0.5;
  const communityMultiplier = Math.max(0.5, Math.min(1.5, 0.5 + communityQuality));
  const social = clamp(baseSocial * communityMultiplier);

  const backers = clamp(byProvider.raises || 0);
  const pedigree = clamp(byProvider.pedigree || 0);
  const dev = clamp(byProvider.github || 0);

  const momentumScore = clamp(byProvider.momentum || 0);
  const momentumPoints = momentumScore * CATEGORY_WEIGHTS.momentum;
  const revenuePoints = byProvider.defillama === 1 ? CATEGORY_WEIGHTS.revenue : 0;

  const socialPoints = social * CATEGORY_WEIGHTS.social;
  const backersPoints = backers * CATEGORY_WEIGHTS.backers;
  const pedigreePoints = pedigree * CATEGORY_WEIGHTS.pedigree;
  const devPoints = dev * CATEGORY_WEIGHTS.dev;

  return {
    social: socialPoints.toFixed(1),
    backers: backersPoints.toFixed(1),
    pedigree: pedigreePoints.toFixed(1),
    dev: devPoints.toFixed(1),
    momentum: momentumPoints.toFixed(1),
    revenue: revenuePoints.toFixed(1),
  };
}
